<template>
    <ul
        ref="containerRef"
        class="nav-links"
        data-test="nav-links">
        <li
            v-for="(link, index) in links"
            :key="link.key"
            :ref="(el) => setItemRef(el, index)"
            :class="{
                'nav-links__item--with-divider': link.divider && isItemVisible(index),
                'nav-links__item--hidden': !isItemVisible(index),
                'nav-links__item--admin': link.isAdmin,
            }"
            :data-visible="isItemVisible(index)"
            class="nav-links__item">
            <a
                :aria-current="link.active ? 'page' : undefined"
                :class="{ 'nav-links__anchor--active': link.active }"
                :data-test="`nav-link-${link.key}`"
                :href="link.href"
                :rel="link.external ? 'noreferrer noopener' : undefined"
                :target="link.external ? '_blank' : undefined"
                class="nav-links__anchor">
                <SkyText
                    class="nav-links__label"
                    tag="span">
                    {{ link.label }}
                </SkyText>
            </a>
        </li>

        <!-- More dropdown for overflow items (always rendered for measurement, hidden when empty) -->
        <li
            ref="moreButtonRef"
            :class="{ 'nav-links__item--hidden': overflowLinks.length === 0 }"
            class="nav-links__item nav-links__item--more">
            <SkyPopoverMenu v-model:active="moreMenuOpen">
                <!-- @vue-ignore -->
                <template #trigger="{ attrs, handlers }">
                    <a
                        class="nav-links__anchor nav-links__anchor--more"
                        data-test="nav-more-trigger"
                        href="#"
                        v-bind="attrs"
                        v-on="handlers"
                        @click.prevent>
                        <SkyText
                            class="nav-links__label"
                            tag="span">
                            {{ moreLabel }}
                        </SkyText>
                        <SkyIconMenuMore
                            variant="dark"
                            size="sm"
                            decorative>
                        </SkyIconMenuMore>
                    </a>
                </template>

                <!-- @vue-ignore -->
                <template #menu="{ attrs }">
                    <SkyDropdownMenu
                        data-test="nav-more-menu"
                        v-bind="attrs">
                        <SkyDropdownMenuItem
                            v-for="link in overflowLinks"
                            :key="link.key"
                            :data-test="`nav-more-link-${link.key}`"
                            :href="link.href"
                            :target="link.external ? '_blank' : undefined">
                            {{ link.label }}
                        </SkyDropdownMenuItem>
                    </SkyDropdownMenu>
                </template>
            </SkyPopoverMenu>
        </li>
    </ul>
</template>

<script lang="ts">
    export interface INavLink {
        key: string;
        label: string;
        href: string;
        active: boolean;
        external: boolean;
        divider: boolean;
        isAdmin: boolean;
    }

    export interface INavLinksProps {
        links: INavLink[];
    }
</script>

<script lang="ts" setup>
    import { BREAKPOINT } from "@eg_ui/src/constants/breakpoints";
    import { injectI18n } from "@eg_ui/src/translations/i18n";
    import { useDebounceFn, useResizeObserver, useWindowSize } from "@vueuse/core";
    import { computed, onMounted, onUnmounted, ref, watch } from "vue";

    import { SkyDropdownMenu, SkyDropdownMenuItem, SkyIconMenuMore, SkyPopoverMenu, SkyText } from "@skyline2/vue";

    const props = defineProps<INavLinksProps>();

    const intl = injectI18n();

    const moreLabel = computed(() =>
        intl.formatMessage({
            defaultMessage: "More",
            id: "navigation.NavLinks.more",
        }),
    );

    const HIDDEN_CLASS = "nav-links__item--hidden";

    const containerRef = ref<HTMLElement | null>(null);
    const moreButtonRef = ref<HTMLElement | null>(null);
    const itemRefs = ref<(HTMLElement | null)[]>([]);
    const moreMenuOpen = ref<boolean>(false);

    const visibleCount = ref<number>(props.links.length);
    const wasMoreVisible = ref<boolean>(false);

    let pendingRefCalculation: ReturnType<typeof setTimeout> | null = null;

    const { width: windowWidth } = useWindowSize();

    /**
     * Vue calls this once per <li> during render. The setTimeout batches these into a single calculation that runs
     * after all refs are populated.
     */
    function setItemRef(el: unknown, index: number) {
        if (el instanceof HTMLElement) {
            itemRefs.value[index] = el;
            // Schedule calculation after all refs are set
            if (windowWidth.value >= BREAKPOINT.MD && !pendingRefCalculation) {
                pendingRefCalculation = setTimeout(() => {
                    pendingRefCalculation = null;
                    calculateVisibleItems();
                }, 0);
            }
        }
    }

    function isItemVisible(index: number): boolean {
        const link = props.links[index];
        if (!link) {
            return false;
        }

        // Admin links hidden entirely below XL breakpoint
        if (link.isAdmin && windowWidth.value < BREAKPOINT.XL) {
            return false;
        }

        return index < visibleCount.value;
    }

    const overflowLinks = computed<INavLink[]>(() => {
        return props.links.filter((link, index) => {
            if (link.isAdmin && windowWidth.value < BREAKPOINT.XL) {
                return false;
            }
            return index >= visibleCount.value;
        });
    });

    /**
     * Measure an element's width.
     *
     * If the element has the hidden class, temporarily makes it measurable
     * (while keeping it invisible) to get an accurate width.
     */
    function measureWidth(el: HTMLElement): number {
        const wasHidden = el.classList.contains(HIDDEN_CLASS);

        if (wasHidden) {
            el.style.visibility = "hidden";
            el.style.position = "absolute";
            el.classList.remove(HIDDEN_CLASS);
        }

        const width = el.offsetWidth;

        if (wasHidden) {
            el.classList.add(HIDDEN_CLASS);
            el.style.visibility = "";
            el.style.position = "";
        }

        return width;
    }

    /**
     * Pack as many items as fit in available width.
     */
    function packItems(widths: number[], available: number, gap: number): number {
        let usedWidth = 0;
        let packed = 0;
        for (const w of widths) {
            const needed = usedWidth + w + (packed > 0 ? gap : 0);
            if (needed > available) {
                break;
            }
            usedWidth = needed;
            packed++;
        }
        return packed;
    }

    function calculateVisibleItems() {
        // Mobile: hamburger menu handles it
        if (windowWidth.value < BREAKPOINT.MD) {
            visibleCount.value = props.links.length;
            return;
        }

        const containerWidth = containerRef.value?.parentElement?.clientWidth ?? containerRef.value?.clientWidth ?? 0;
        if (containerWidth <= 0) {
            visibleCount.value = props.links.length;
            return;
        }

        // Get displayable indices (exclude admin links below XL)
        const displayableIndices = props.links
            .map((link, i) => (link.isAdmin && windowWidth.value < BREAKPOINT.XL ? -1 : i))
            .filter((i) => i >= 0);

        // Check refs are ready
        if (displayableIndices.some((idx) => !itemRefs.value[idx])) {
            debouncedCalculate();
            return;
        }

        const gap = containerRef.value ? parseFloat(getComputedStyle(containerRef.value).gap) || 36 : 36;
        const moreWidth = moreButtonRef.value ? measureWidth(moreButtonRef.value) : 80;

        // Measure all items
        const widths = displayableIndices.map((idx) => measureWidth(itemRefs.value[idx]!));
        const totalWidth = widths.reduce((sum, w, i) => sum + w + (i > 0 ? gap : 0), 0);

        // Hysteresis: require extra space before hiding More button
        const SLACK = 24;
        const hysteresis = wasMoreVisible.value ? moreWidth + gap : 0;

        // Everything fits
        if (totalWidth <= containerWidth - SLACK - hysteresis) {
            visibleCount.value = props.links.length;
            wasMoreVisible.value = false;
            return;
        }

        const SAFE_BUFFER = 40;
        const available = Math.max(containerWidth - moreWidth - gap - SAFE_BUFFER, 0);
        const packed = packItems(widths, available, gap);

        const newVisibleCount =
            packed >= displayableIndices.length ? props.links.length : (displayableIndices[packed] ?? 0);

        visibleCount.value = newVisibleCount;
        wasMoreVisible.value = newVisibleCount < props.links.length;
    }

    const debouncedCalculate = useDebounceFn(calculateVisibleItems, 20);

    useResizeObserver(containerRef, debouncedCalculate);

    watch(() => [props.links, windowWidth.value], debouncedCalculate, { deep: true, immediate: true });

    onMounted(() => {
        requestAnimationFrame(calculateVisibleItems);
    });

    onUnmounted(() => {
        if (pendingRefCalculation) {
            clearTimeout(pendingRefCalculation);
        }
    });
</script>

<style lang="scss">
    @use "./NavLinks.scss";
</style>
